<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { staticPageHref } from '@/router/products'
import type { Locale } from '@/i18n'
import { useAuth } from '@/composables/useAuth'
import AppButton from './AppButton.vue'

/** `collapsible` : carte repliable (titre cliquable + chevron) comme « Vos choix ».
 *  Utilisé dans le parcours (arbre de décision), où elle est repliée par défaut. */
const props = withDefaults(defineProps<{ collapsible?: boolean }>(), {
  collapsible: false,
})

const { t, locale } = useI18n()
const router = useRouter()
const { login } = useAuth()
/** Lien localisé vers la page « mot de passe oublié ». */
const forgotHref = computed(() => staticPageHref('forgotPassword', locale.value as Locale))
/** Lien localisé vers l'espace personnel (cible après connexion). */
const accountHref = computed(() => staticPageHref('account', locale.value as Locale))
/** Lien localisé vers la page de connexion, où se fait la saisie du code. */
const loginHref = computed(() => staticPageHref('login', locale.value as Locale))
/** Repliée par défaut quand collapsible ; toujours visible sinon. */
const isOpen = ref(false)
const bodyVisible = computed(() => !props.collapsible || isOpen.value)
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)

/** Erreurs par champ (affichées sous l'input) + erreur générale (échec de connexion). */
const emailError = ref<string | null>(null)
const passwordError = ref<string | null>(null)
const generalError = ref<string | null>(null)

/** RFC 5322 « simplifiée » : suffisant pour un contrôle de saisie côté client. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validateEmail(): string | null {
  if (!email.value.trim()) return t('auth.login.errorEmailRequired')
  if (!EMAIL_RE.test(email.value.trim())) return t('auth.login.errorEmailInvalid')
  return null
}

// Même logique que la page /connexion (LoginView) : validation par champ, appel
// useAuth().login(), mapping du résultat en message localisé, puis redirection
// vers l'espace personnel en cas de succès.
async function submit() {
  emailError.value = validateEmail()
  passwordError.value = password.value ? null : t('auth.login.errorPasswordRequired')
  generalError.value = null
  if (emailError.value || passwordError.value) return

  isLoading.value = true
  const result = await login(email.value.trim(), password.value)
  isLoading.value = false

  if (result === 'success') {
    router.push(accountHref.value)
    return
  }
  if (result === 'mfa_required') {
    // Le mot de passe est bon : il reste le code reçu par email, saisi sur
    // /connexion. La connexion en cours vit dans useAuth, donc LoginView la
    // reprend telle quelle — cette carte n'a pas à dupliquer l'écran de code.
    password.value = ''
    router.push(loginHref.value)
    return
  }

  generalError.value =
    result === 'account_blocked'
      ? t('auth.login.errorBlocked')
      : result === 'rate_limited'
        ? t('auth.login.errorRateLimited')
        : result === 'network_error'
          ? t('auth.login.errorNetwork')
          : t('auth.login.errorInvalid')
}
</script>

<template>
  <section class="login-card" aria-labelledby="login-title">
    <!-- Repliable (parcours) : titre cliquable + chevron -->
    <h2 v-if="collapsible" id="login-title" class="login-card__title-toggle">
      <button
        type="button"
        class="login-card__toggle"
        :aria-expanded="isOpen"
        aria-controls="login-card-body"
        @click="isOpen = !isOpen"
      >
        <span class="login-card__toggle-head">
          <span class="eyebrow">{{ t('subscription.login.eyebrow') }}</span>
          <span class="login-card__toggle-title">{{ t('subscription.login.title') }}</span>
        </span>
        <svg
          class="login-card__chevron"
          :class="{ 'is-open': isOpen }"
          viewBox="0 0 20 20"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </h2>

    <!-- Statique (accueil) -->
    <header v-else class="login-card__head">
      <span class="eyebrow">{{ t('subscription.login.eyebrow') }}</span>
      <h2 id="login-title">{{ t('subscription.login.title') }}</h2>
      <p class="login-card__desc">{{ t('subscription.login.description') }}</p>
    </header>

    <div v-show="bodyVisible" id="login-card-body" class="login-card__body">
    <p v-if="collapsible" class="login-card__desc">{{ t('subscription.login.description') }}</p>
    <form class="login-card__form" novalidate @submit.prevent="submit">
      <p class="login-card__required-legend">
        <span class="login-card__required" aria-hidden="true">*</span>
        {{ t('common.requiredLegend') }}
      </p>
      <div class="login-card__field">
        <label for="login-email">
          {{ t('subscription.login.email') }}
          <span class="login-card__required" :aria-label="t('common.requiredMark')">*</span>
        </label>
        <input
          id="login-email"
          v-model="email"
          type="email"
          autocomplete="email"
          required
          :disabled="isLoading"
          :aria-invalid="emailError ? 'true' : undefined"
          :aria-describedby="emailError ? 'login-email-error' : undefined"
          @input="emailError = null"
        />
        <p v-if="emailError" id="login-email-error" class="login-card__error" role="alert">
          {{ emailError }}
        </p>
      </div>

      <div class="login-card__field">
        <div class="login-card__field-row">
          <label for="login-password">
            {{ t('subscription.login.password') }}
            <span class="login-card__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <a :href="forgotHref" class="login-card__link">{{ t('subscription.login.forgot') }}</a>
        </div>
        <div class="login-card__input-wrap">
          <input
            id="login-password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="current-password"
            required
            :disabled="isLoading"
            :aria-invalid="passwordError ? 'true' : undefined"
            :aria-describedby="passwordError ? 'login-password-error' : undefined"
            @input="passwordError = null"
          />
          <button
            type="button"
            class="login-card__reveal"
            :aria-label="showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')"
            :aria-pressed="showPassword"
            @click="showPassword = !showPassword"
          >
            <!-- Œil ouvert (mot de passe visible) -->
            <svg v-if="showPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"/>
            </svg>
            <!-- Œil barré (mot de passe masqué, défaut) -->
            <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M2 12s3.5-7 10-7c2 0 3.8.66 5.3 1.6M22 12s-3.5 7-10 7c-2 0-3.8-.66-5.3-1.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4 4l16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <p v-if="passwordError" id="login-password-error" class="login-card__error" role="alert">
          {{ passwordError }}
        </p>
      </div>

      <p v-if="generalError" class="login-card__error login-card__error--banner" role="alert">
        {{ generalError }}
      </p>

      <AppButton type="submit" variant="dark" size="md" block :ring="!isLoading">
        {{ isLoading ? t('subscription.login.submitting') : t('subscription.login.submit') }}
      </AppButton>
    </form>
    </div>
  </section>
</template>

<style scoped>
.login-card {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background: var(--color-white);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

.login-card__head {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.login-card__head h2 {
  font-size: var(--text-xl);
}
.login-card__desc {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

/* ---- Variante repliable (parcours) ---- */
.login-card__title-toggle {
  margin: 0;
  font-size: inherit;
}
.login-card__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0;
  background: none;
  border: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.login-card__toggle:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 4px;
  border-radius: var(--radius-xs);
}
.login-card__toggle-head {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
}
.login-card__toggle-title {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
  color: var(--text);
}
.login-card__chevron {
  flex-shrink: 0;
  color: var(--text-muted);
  transition: transform var(--dur-fast) var(--ease-out);
}
.login-card__chevron.is-open {
  transform: rotate(180deg);
}

.login-card__body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.login-card__form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.login-card__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.login-card__field-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.login-card__field label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
}
.login-card__required {
  color: var(--danger);
  font-weight: 700;
  margin-left: 0.15em;
}
.login-card__required-legend {
  display: flex;
  align-items: baseline;
  gap: 0.35em;
  font-size: var(--text-xs);
  color: var(--text-muted);
}
.login-card__required-legend .login-card__required {
  margin-left: 0;
}
.login-card__field input {
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--bg-soft);
  font-size: var(--text-base);
  transition: border-color var(--dur-fast), background var(--dur-fast);
}
.login-card__field input:focus {
  outline: none;
  border-color: var(--primary);
  background: var(--color-white);
}

/* Champ mot de passe : input + bouton œil superposé à droite. */
.login-card__input-wrap {
  position: relative;
  display: flex;
}
.login-card__input-wrap input {
  width: 100%;
  padding-right: 3rem;
}
.login-card__reveal {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 3rem;
  display: grid;
  place-items: center;
  background: none;
  border: 0;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color var(--dur-fast) var(--ease-out);
}
.login-card__reveal:hover { color: var(--color-chocolate); }
.login-card__reveal:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}

.login-card__link {
  font-size: var(--text-xs);
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.18em;
  transition: color var(--dur-fast);
}
.login-card__link:hover { color: var(--color-cherry); }
.login-card__link--strong {
  font-weight: 700;
  font-size: var(--text-sm);
}

.login-card__error {
  font-size: var(--text-sm);
  color: var(--color-cherry);
}
/* Erreur générale (échec de connexion) : présentée en bannière. */
.login-card__error--banner {
  background: color-mix(in oklab, var(--color-cherry) 8%, white);
  padding: 0.625rem 0.75rem;
  border-radius: var(--radius-sm);
}

</style>
